import { Icon, Stack, Typography } from '@mui/material';
import { useNotifications } from '@schooly/components/notifications';
import { useFileDropZone } from '@schooly/hooks/use-file-drop-zone';
import { DropZoneBlock, ImageFormatIcon, ImageSizeIcon } from '@schooly/style';
import { FC, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  FormSchoolLogoParam,
  FormSchoolLogoPreview,
  FormSchoolLogoStyled,
} from './FormSchoolLogoField';

export interface FormSchoolLogoProps {
  onChange?: (file?: File) => void;
  error?: boolean;
  errorMessage?: string;
  width?: number;
  content?: React.ReactNode;
}

const MAX_SIZE_KB = 200;
const MAX_SIZE = MAX_SIZE_KB * 1024;
const IMAGE_ACCEPT = { 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'] };
const IMAGE_FORMAT_LABEL = '.png, .jpeg';

export const FormSchoolLogo: FC<FormSchoolLogoProps> = ({
  error: errorProp,
  errorMessage,
  onChange,
  width = 300,
  content,
}) => {
  const { formatMessage } = useIntl();
  const { showError } = useNotifications();

  const { getRootProps, getInputProps, isDragActive, hasError, file } = useFileDropZone({
    accept: IMAGE_ACCEPT,
    maxSize: MAX_SIZE,
    onError: (message) => showError({ message }),
    errorMessages: {
      fileTooLarge: formatMessage({ id: 'dnd-Error-FileIsLarger' }, { size: `${MAX_SIZE_KB} KB` }),
      fileInvalidType: formatMessage(
        { id: 'dnd-Error-InvalidFormat' },
        { format: IMAGE_FORMAT_LABEL },
      ),
    },
  });

  const preview = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);

  const isError = errorProp || hasError;

  useEffect(() => {
    if (errorMessage) {
      showError({ message: errorMessage });
    }
  }, [errorMessage, showError]);

  useEffect(() => {
    onChange?.(file);
  }, [file, onChange]);

  return (
    <FormSchoolLogoStyled width={width}>
      <DropZoneBlock
        {...getRootProps()}
        active={isDragActive}
        error={isError}
        gap={2.5}
        sx={{
          width,
          height: width,
          borderRadius: '50%',
          overflow: 'hidden',
          position: 'relative',
          p: 0,
        }}
      >
        {preview ? (
          <FormSchoolLogoPreview preview={preview} />
        ) : (
          <>
            <input {...getInputProps()} />
            {content ?? (
              <>
                <Stack alignItems="center" justifyContent="center">
                  <Typography color="inherit" variant="h3">
                    {formatMessage({ id: 'dnd-Image' })}
                  </Typography>
                  <Typography color="inherit">
                    {formatMessage(
                      { id: 'dnd-BrowseAFile' },
                      {
                        browse: (
                          <Typography component="span" color="primary" key="browse">
                            {formatMessage({ id: 'dnd-Browse' })}
                          </Typography>
                        ),
                      },
                    )}
                  </Typography>
                </Stack>

                <Stack alignItems="center" justifyContent="center" gap={1}>
                  <FormSchoolLogoParam variant="caption">
                    <Icon fontSize="small">
                      <ImageSizeIcon />
                    </Icon>
                    <span>
                      {formatMessage({ id: 'dnd-MaxSize' }, { size: `${MAX_SIZE_KB} KB` })}
                    </span>
                  </FormSchoolLogoParam>
                  <FormSchoolLogoParam variant="caption">
                    <Icon fontSize="small">
                      <ImageFormatIcon />
                    </Icon>
                    <span>
                      {formatMessage({ id: 'dnd-Format' }, { format: IMAGE_FORMAT_LABEL })}
                    </span>
                  </FormSchoolLogoParam>
                </Stack>
              </>
            )}
          </>
        )}
      </DropZoneBlock>
    </FormSchoolLogoStyled>
  );
};
